import { getExpertInfo } from '@/features/chat/utils/getExpertInfo';
import { ExpertRole } from '@/features/experts/types/ExpertRole';
import { useExpertsStore } from '@/utils/stores/useExpertsStore';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuthStore } from '../../../../../utils/stores/useAuthStore';
import { Message } from '../../../../chat/types/Message';
import VenereSimpleLayout from '../../shared/pages/VenereSimpleLayout';
import { VenereTextarea } from '../../shared/ui';
import VenereMessageBubble from '../components/VenereMessageBubble';
import { useVenereChatSessions, useVenereMessages, useVenereSendMessage } from '../hooks';
import { VenereChatSession } from '../types/VenereChatSession';

const VenereChatScreen = () => {
  const { chatSessionId } = useParams();
  const navigate = useNavigate();
  const { auth } = useAuthStore();
  const { data: chatSessions, isLoading, isFetching } = useVenereChatSessions();
  const { experts } = useExpertsStore();
  const [currentSession, setCurrentSession] = useState<VenereChatSession | null>(null);
  const [expertInfo, setExpertInfo] = useState<any>(null);
  const [messageText, setMessageText] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Chat hooks
  const {
    messages,
    isLoading: messagesLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isError: messagesError,
  } = useVenereMessages(chatSessionId || '', true, 3000);

  const { sendMessage, isPending: isSending } = useVenereSendMessage(
    chatSessionId || '',
    () => {
      setMessageText('');
      // Reset textarea height
      if (inputRef.current) {
        inputRef.current.style.height = 'auto';
      }
      scrollToBottom();
      // Refocus input after sending message
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  );

  useEffect(() => {
    if (chatSessionId && chatSessions) {
      const session = chatSessions.find(
        (session: VenereChatSession) => session.id === chatSessionId,
      );
      setCurrentSession(session || null);
    }
  }, [chatSessionId, chatSessions]);

  useEffect(() => {
    if (currentSession && experts) {
      try {
        const info = getExpertInfo(currentSession.expert_id, experts);
        setExpertInfo(info);
      } catch (error) {
        console.error('Error getting expert info:', error);
        setExpertInfo(null);
      }
    }
  }, [currentSession, experts]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Focus input when component mounts
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  // Improved mobile detection
  const isMobileDevice = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || 
           window.innerWidth <= 768;
  };

  const handleBackClick = () => {
    navigate('/venere/chat');
  };

  const handleSendMessage = () => {
    if (messageText.trim() && chatSessionId) {
      sendMessage(messageText.trim());
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    // Check if it's a mobile device
    const isMobile = isMobileDevice();
    
    if (e.key === 'Enter') {
      if (isMobile) {
        // On mobile: Enter creates new line, no sending
        return;
      } else {
        // On desktop: Enter sends message, Shift+Enter creates new line
        if (e.shiftKey) {
          // Shift+Enter: Allow new line
          return;
        } else {
          // Enter: Send message
          e.preventDefault();
          handleSendMessage();
        }
      }
    }
  };

  // Auto-resize textarea based on content
  const handleTextareaChange = (value: string) => {
    setMessageText(value);
  };

  const isUserMessage = (message: Message) => {
    return message.sender_id === auth.userId;
  };

  if (isLoading) {
    return (
      <VenereSimpleLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="w-8 h-8 border-4 border-venere-royal border-t-transparent rounded-full animate-spin"></div>
        </div>
      </VenereSimpleLayout>
    );
  }

  if (!chatSessionId) {
    return (
      <VenereSimpleLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="venere-text-primary mb-4">Nessuna sessione di chat selezionata</div>
            <button 
              onClick={handleBackClick}
              className="px-4 py-2 bg-venere-royal text-white rounded-lg hover:opacity-90 transition-opacity"
            >
              Torna alla lista
            </button>
          </div>
        </div>
      </VenereSimpleLayout>
    );
  }

  if (!currentSession) {
    if (isFetching) {
      return (
        <VenereSimpleLayout>
          <div className="flex items-center justify-center min-h-screen">
            <div className="w-8 h-8 border-4 border-venere-royal border-t-transparent rounded-full animate-spin"></div>
          </div>
        </VenereSimpleLayout>
      );
    } else {
      return (
        <VenereSimpleLayout>
          <div className="flex items-center justify-center min-h-screen">
            <div className="text-center">
              <div className="venere-text-primary mb-4">Sessione di chat non trovata</div>
              <button 
                onClick={handleBackClick}
                className="px-4 py-2 bg-venere-royal text-white rounded-lg hover:opacity-90 transition-opacity"
              >
                Torna alla lista
              </button>
            </div>
          </div>
        </VenereSimpleLayout>
      );
    }
  }

  return (
    <VenereSimpleLayout 
      showBackButton={true}
      onBackClick={handleBackClick}
      backButtonText="Torna alla lista"
      showUserAvatar={true}
      expertInfo={expertInfo ? {
        name: expertInfo.expertName,
        familyName: expertInfo.expertFamilyName,
        role: expertInfo.expertRole,
        avatar: expertInfo.expertAvatar || "/images/assets/LOGO_Nero.svg"
      } : undefined}
    >
      <div className="fixed top-20 md:top-16 left-0 right-0 bottom-0 flex flex-col">
        {/* Chat Messages Area - Scrollable */}
        <div className="flex-1 overflow-y-auto bg-gray-50">
          <div className="p-4">
            {messagesLoading ? (
              <div className="flex items-center justify-center h-full">
                <div className="w-8 h-8 border-4 border-venere-royal border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : messagesError ? (
              <div className="text-center text-red-500">
                Errore nel caricamento dei messaggi
              </div>
            ) : messages && messages.length > 0 ? (
              <div className="space-y-2">
                {messages.map((message: Message) => {
                  const isUser = isUserMessage(message);
                  return (
                    <VenereMessageBubble
                      key={message.id}
                      message={message.message}
                      timestamp={message.created_at}
                      isUser={isUser}
                    />
                  );
                })}
                <div ref={messagesEndRef} />
              </div>
            ) : (
              <div className="text-center text-gray-500">
                <div className="venere-text-secondary">
                  Chat con {expertInfo ? expertInfo.expertRole === ExpertRole.Admin ? "Customer Care" : `${expertInfo.expertName} ${expertInfo.expertFamilyName}` : 'Esperto'}
                </div>
                <div className="text-sm mt-2">
                  Sessione creata il {new Date(currentSession.created_at).toLocaleDateString('it-IT', {
                    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
                  })}
                </div>
                <div className="mt-4 text-sm">
                  Inizia la conversazione inviando un messaggio
                </div>
              </div>
            )}
            
            {/* Load more messages button */}
            {hasNextPage && (
              <div className="text-center mt-4">
                <button
                  onClick={() => fetchNextPage()}
                  disabled={isFetchingNextPage}
                  className="px-4 py-2 text-sm text-venere-royal hover:underline disabled:opacity-50"
                >
                  {isFetchingNextPage ? 'Caricamento...' : 'Carica messaggi precedenti'}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Message Input Area - Fixed at bottom */}
        <div className="flex-shrink-0 bg-white border-t border-gray-200 p-4 mt-auto">
          <div className="flex gap-2 items-end">
            <VenereTextarea
              ref={inputRef}
              value={messageText}
              onChange={handleTextareaChange}
              onKeyPress={handleKeyPress}
              placeholder="Scrivi un messaggio..."
              disabled={isSending}
              maxLength={500}
              minRows={1}
              maxRows={4}
              autoResize={true}
              size="sm"
              className="flex-1"
            />
            <div className="flex flex-col items-end gap-1">
              {/* Character counter */}
              <div className={`text-xs ${
                messageText.length > 450 
                  ? 'text-red-500' 
                  : messageText.length > 400 
                    ? 'text-orange-500' 
                    : 'text-gray-500'
              }`}>
                {messageText.length}/500
              </div>
              <button
                onClick={handleSendMessage}
                disabled={!messageText.trim() || isSending}
                className="px-6 py-2 venere-bg-royal text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 whitespace-nowrap"
              >
                {isSending ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  'Invia'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </VenereSimpleLayout>
  );
};

export default VenereChatScreen; 